import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { neon, User } from "@/integrations/neon";
import { getErrorMessage } from "@/utils/errors";

interface PasswordResetFormProps {
  onSuccess: (user: User | null) => void;
  onCancel?: () => void;
}

export const PasswordResetForm = ({ onSuccess, onCancel }: PasswordResetFormProps) => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash) {
      const newUrl = window.location.pathname + window.location.search;
      window.history.replaceState(null, document.title, newUrl);
    }
  }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (password.length < 8) {
      toast({
        title: "Password too short",
        description: "Password must be at least 8 characters.",
        variant: "destructive",
      });
      return;
    }

    if (password !== confirmPassword) {
      toast({
        title: "Passwords do not match",
        description: "Please make sure both password fields match.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { error: updateError } = await neon.auth.updateUser({ password });
      if (updateError) throw updateError;

      const { data: { user }, error: userError } = await neon.auth.getUser();
      if (userError) throw userError;

      toast({
        title: "Password updated",
        description: "Your password has been reset successfully.",
      });

      onSuccess(user ?? null);
    } catch (error: unknown) {
      toast({
        title: "Password reset failed",
        description: getErrorMessage(error, "Something went wrong. Please try again."),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-subtle flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="shadow-strong border-0">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl text-center">Set a new password</CardTitle>
            <CardDescription className="text-center">
              Choose a strong password to secure your account.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={handleSubmit}>
              <div className="space-y-2">
                <Label htmlFor="new-password">New password</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="Enter new password"
                  minLength={8}
                  required
                  className="transition-all duration-200 focus:shadow-soft"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  placeholder="Re-enter new password"
                  minLength={8}
                  required
                  className="transition-all duration-200 focus:shadow-soft"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-gradient-primary hover:opacity-90 transition-all duration-200 shadow-medium hover:shadow-strong"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Updating..." : "Update password"}
              </Button>
            </form>
            {onCancel && (
              <Button
                type="button"
                variant="ghost"
                className="w-full mt-4 text-sm text-muted-foreground"
                onClick={onCancel}
                disabled={isSubmitting}
              >
                Cancel and return to sign in
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
